import { useCallback } from "react";

import { toast } from "@performyard/py-components";

import { useCreateCompanyForm } from "../../../api/hooks";

import type { PostCompanyReviewFormResponseJson } from "../../../api/services/defs/internal-api/company-forms";

import { transformFormSuggestionToRequest } from "../utils/transformFormSuggestion";

import { useAskPerformYardStore } from "../AskPerformYardStore";

/** Shared hook backing the FormPreviewCard's Create Form button. The post-create
 *  "View Form" button opens the builder via window.open in the card itself, so
 *  this hook only owns the create + delete flow. */
export const useFormCreation = () => {
	const formSuggestionStates = useAskPerformYardStore((state) => state.formSuggestionStates);
	const markFormCreated = useAskPerformYardStore((state) => state.markFormCreated);
	const markFormDeleted = useAskPerformYardStore((state) => state.markFormDeleted);
	const markFormCreating = useAskPerformYardStore((state) => state.markFormCreating);
	const clearFormCreating = useAskPerformYardStore((state) => state.clearFormCreating);

	const createFormMutation = useCreateCompanyForm();

	const createForm = useCallback(
		async (suggestionId: string) => {
			const fs = formSuggestionStates.find((s) => s.suggestion.id === suggestionId);
			// Form creation isn't page-tethered, so a restored card can still create —
			// see useGoalCreation for the same reasoning.
			if (!fs || fs.status !== "pending") return;

			markFormCreating(suggestionId);
			try {
				const payload = transformFormSuggestionToRequest(fs.suggestion);
				const response: PostCompanyReviewFormResponseJson = await createFormMutation.mutateAsync(payload);
				const newId = response?._id ? String(response._id) : null;
				if (!newId) {
					throw new Error("Form creation succeeded but no ID was returned");
				}
				// _id === base_id for newly created forms (server sets base_id = _id on insert).
				markFormCreated(suggestionId, newId, newId);
			} catch {
				toast.error("Failed to create form");
			} finally {
				clearFormCreating(suggestionId);
			}
		},
		[formSuggestionStates, createFormMutation, markFormCreating, markFormCreated, clearFormCreating]
	);

	const deleteForm = useCallback(
		(suggestionId: string) => {
			markFormDeleted(suggestionId);
		},
		[markFormDeleted]
	);

	return { createForm, deleteForm };
};
